/**
 * Analytics internos SoluQR (ingesta ligera, fire-and-forget).
 * TODO: embudos y paneles visuales en dashboard
 * TODO: retención y cohortes
 * TODO: heatmaps (privacy-first)
 * TODO: A/B testing server-side
 */
import { isDemoBusinessId } from '@/lib/demo/demoBusiness';
import { hasAnalyticsConsent } from '@/lib/consent/cookieConsent';

const SESSION_KEY = 'soluqr_analytics_session_id';

function getOrCreateSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (s && s.length <= 128) return s;
    s =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(SESSION_KEY, s);
    return s;
  } catch {
    return `sess_${Date.now()}`;
  }
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} [metadata] — puede incluir `business_id` (se envía aparte)
 */
export function trackEvent(eventName, metadata = {}) {
  if (typeof window === 'undefined' || !eventName) return;
  if (!hasAnalyticsConsent()) return;

  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? { ...metadata } : {};
  let businessId = meta.business_id;
  delete meta.business_id;
  if (businessId != null) {
    businessId = String(businessId).trim() || undefined;
  } else {
    businessId = undefined;
  }

  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (!base) return;

  const payload = {
    event_name: String(eventName),
    ...(businessId ? { business_id: businessId } : {}),
    session_id: getOrCreateSessionId(),
    metadata: meta,
  };

  const headers = {
    'Content-Type': 'application/json',
    'X-SoluQR-Route': window.location.pathname || '/',
    'X-SoluQR-Source': 'web',
  };
  try {
    const t = localStorage.getItem('authToken');
    if (t) headers.Authorization = `Bearer ${t}`;
  } catch {
    /* ignore */
  }

  try {
    void fetch(`${base}/api/analytics/event`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export function trackMenuView(businessId) {
  if (!businessId) return;
  trackEvent('menu_view', { business_id: businessId });
}

export function trackProductView(productId, businessId) {
  if (!productId || !businessId) return;
  const demo = isDemoBusinessId(businessId);
  trackEvent('product_view', {
    business_id: businessId,
    product_id: String(productId),
    ...(demo ? { is_demo: true } : {}),
  });
}

export function trackProductLike(productId, businessId) {
  if (!productId || !businessId) return;
  const demo = isDemoBusinessId(businessId);
  trackEvent('product_like', {
    business_id: businessId,
    product_id: String(productId),
    ...(demo ? { is_demo: true } : {}),
  });
}

export function trackProductUnlike(productId, businessId) {
  if (!productId || !businessId) return;
  const demo = isDemoBusinessId(businessId);
  trackEvent('product_unlike', {
    business_id: businessId,
    product_id: String(productId),
    ...(demo ? { is_demo: true } : {}),
  });
}

export function trackAddToCart(productId, businessId) {
  if (!productId || !businessId) return;
  const demo = isDemoBusinessId(businessId);
  trackEvent('add_to_cart', {
    business_id: businessId,
    product_id: String(productId),
    ...(demo ? { is_demo: true } : {}),
  });
}

export function trackCheckoutStarted(businessId, extra = {}) {
  if (!businessId) return;
  const demo = isDemoBusinessId(businessId);
  if (demo) {
    trackEvent('checkout_demo_started', { business_id: businessId, is_demo: true, ...extra });
  } else {
    trackEvent('checkout_started', { business_id: businessId, ...extra });
  }
}

/** Demo: interacción con el asistente (p. ej. primer mensaje). */
export function trackAssistantUsed(businessId, extra = {}) {
  if (!businessId || !isDemoBusinessId(businessId)) return;
  trackEvent('demo_assistant_used', { business_id: businessId, is_demo: true, ...extra });
}

const LANDING_ACTION_EVENTS = {
  demo: 'landing_click_demo',
  pricing: 'landing_click_pricing',
  register: 'landing_click_register',
  whatsapp: 'landing_click_whatsapp',
  video: 'landing_play_video',
};

export function trackLandingAction(action) {
  const key = String(action || '').trim();
  const eventName = LANDING_ACTION_EVENTS[key] || `landing_click_${key}`;
  trackEvent(eventName, { action: key });
}
